import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";
import { memo, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AppIcon from "@/components/Core/AppIcon";
import { useTranslate } from "@/hooks";
import { colors } from "@/theme";

import { OPTIONS, SPORT_PATH_PREFIX } from "./data";
import { Mode } from "./type";

/** Room between the track's edge and the thumb, in px. */
const INSET = 4;

/**
 * The track. Its two halves are the same width whatever their labels come to
 * in a language, so the thumb is always exactly one half and slides between
 * them rather than resizing as it goes.
 */
const Track = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  padding: INSET,
  borderRadius: 999,
  border: "1px solid",
  borderColor: alpha(colors.prime, 0.22),
  backgroundColor: alpha(colors.prime, 0.06),
  ...theme.applyStyles("dark", { backgroundColor: alpha(colors.prime, 0.1) }),
}));

/** The filled half, which slides to whichever side is on. */
const Thumb = styled(Box, {
  shouldForwardProp: (prop) => prop !== "index",
})<{ index: number }>(({ index }) => ({
  position: "absolute",
  top: INSET,
  bottom: INSET,
  left: INSET,
  width: `calc(50% - ${INSET}px)`,
  borderRadius: 999,
  backgroundColor: colors.prime,
  boxShadow: `0 4px 12px ${alpha(colors.prime, 0.4)}`,
  transform: `translateX(${index * 100}%)`,
  transition: "transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1)",
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

const ToggleButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ theme, active }) => ({
  // Over the thumb, which is positioned and would otherwise cover the label.
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  minHeight: 34,
  padding: "0 18px",
  border: "none",
  cursor: "pointer",
  borderRadius: 999,
  background: "transparent",
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 700,
  whiteSpace: "nowrap",
  color: active ? colors.white : theme.palette.text.secondary,
  transition: "color 200ms ease",
  "& svg": { width: 18, height: 18 },
  "@media (hover: hover)": {
    "&:hover": { color: active ? colors.white : theme.palette.text.primary },
  },
  "&:focus-visible": { outline: "2px solid #5BC8FF", outlineOffset: 2 },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

/**
 * The switch between the casino and the sportsbook, in the header.
 *
 * The side that is on is filled in the brand blue in both color schemes - it
 * was orange in the light one, the only orange control on the site - and the
 * fill slides across when the player changes side, so the two halves read as
 * one switch and not as two buttons that happen to be neighbours.
 */
const CasinoSportToggle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslate();

  const activeMode: Mode = useMemo(
    () => (location.pathname.startsWith(SPORT_PATH_PREFIX) ? "sport" : "casino"),
    [location.pathname],
  );
  const activeIndex = Math.max(
    0,
    OPTIONS.findIndex((option) => option.mode === activeMode),
  );

  return (
    <Track>
      <Thumb index={activeIndex} aria-hidden />
      {OPTIONS.map((option) => (
        <ToggleButton
          key={option.mode}
          type="button"
          active={activeMode === option.mode}
          onClick={() => navigate(option.to)}
          aria-pressed={activeMode === option.mode}
        >
          <AppIcon name={option.icon} size={18} />
          {t(option.label)}
        </ToggleButton>
      ))}
    </Track>
  );
};

export default memo(CasinoSportToggle);
